<template>
  <div class="mail-page">

    <!-- HEADER -->
    <div class="mail-header">
      <div class="mail-header__left">
        <div class="mail-header__icon">
          <i class="fa-solid fa-paper-plane"></i>
        </div>
        <div>
          <h1 class="mail-header__title">Emails</h1>
          <p class="mail-header__sub">Outbound email history &amp; delivery</p>
        </div>
      </div>
      <button
        v-if="$can('send protokol-mail')"
        class="btn-compose"
        @click="showSidebar = true; resetComposeForm()"
      >
        Compose <i class="fa-solid fa-plus"></i>
      </button>
    </div>

    <!-- ALERTS -->
    <b-alert
      v-if="emailsError"
      variant="danger"
      show
      dismissible
      @dismissed="emailsError = null"
    >{{ emailsError }}</b-alert>

    <!-- DETAIL DIALOG -->
    <main-dialog
      :show="showDetailModal"
      title="Email Details"
      max-width="640px"
      @close="showDetailModal = false"
    >
      <div v-if="detailsLoading" class="mail-loading" style="padding:40px 0">
        <b-spinner small></b-spinner> Loading details...
      </div>
      <div v-else-if="detailsError" class="detail-error">{{ detailsError }}</div>
      <div v-else-if="selectedEmail">

        <!-- Header: subject + status + dates -->
        <div class="detail-header">
          <div>
            <div class="detail-subject">{{ selectedEmail.subject }}</div>
            <div class="detail-header__meta">
              <span class="status-badge" :class="'status-badge--' + selectedEmail.status">{{ selectedEmail.status }}</span>
              <span v-if="selectedEmail.read_at" class="detail-tag detail-tag--opened"><i class="fa-solid fa-envelope-open"></i> Opened</span>
              <span v-if="selectedEmail.spam_reported" class="detail-tag detail-tag--spam"><i class="fa-solid fa-flag"></i> Spam</span>
            </div>
          </div>
          <div class="detail-dates">
            <div class="detail-dates__row">
              <span class="detail-dates__label">Created</span>
              <span class="detail-dates__val">{{ formatDetailDate(selectedEmail.created_at) }}</span>
            </div>
            <div v-if="selectedEmail.sent_at" class="detail-dates__row">
              <span class="detail-dates__label">Sent</span>
              <span class="detail-dates__val">{{ formatDetailDate(selectedEmail.sent_at) }}</span>
            </div>
            <div v-if="selectedEmail.read_at" class="detail-dates__row">
              <span class="detail-dates__label">Opened</span>
              <span class="detail-dates__val">{{ formatDetailDate(selectedEmail.read_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Recipients -->
        <div class="detail-section">
          <div class="detail-section__title">Recipients</div>
          <div class="detail-row">
            <span class="detail-row__label">From</span>
            <span class="detail-row__value detail-row__value--mono">{{ selectedEmail.from }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-row__label">To</span>
            <span class="detail-row__value">{{ (selectedEmail.to || []).join(', ') }}</span>
          </div>
          <div v-if="selectedEmail.cc && selectedEmail.cc.length" class="detail-row">
            <span class="detail-row__label">CC</span>
            <span class="detail-row__value">{{ selectedEmail.cc.join(', ') }}</span>
          </div>
          <div v-if="selectedEmail.bcc && selectedEmail.bcc.length" class="detail-row">
            <span class="detail-row__label">BCC</span>
            <span class="detail-row__value">{{ selectedEmail.bcc.join(', ') }}</span>
          </div>
          <div v-if="selectedEmail.reply_to" class="detail-row">
            <span class="detail-row__label">Reply-To</span>
            <span class="detail-row__value detail-row__value--mono">{{ selectedEmail.reply_to }}</span>
          </div>
        </div>

        <!-- Metadata -->
        <div class="detail-section">
          <div class="detail-section__title">Metadata</div>
          <div class="detail-row">
            <span class="detail-row__label">Message ID</span>
            <div class="detail-row__copy-row">
              <span class="detail-row__value detail-row__value--mono">{{ selectedEmail.message_id }}</span>
              <button class="detail-copy-btn" @click="copyToClipboard(selectedEmail.message_id)"><i class="fa-solid fa-copy"></i></button>
            </div>
          </div>
          <div v-if="selectedEmail.sender_name" class="detail-row">
            <span class="detail-row__label">Sender name</span>
            <span class="detail-row__value">{{ selectedEmail.sender_name }}</span>
          </div>
          <div v-if="selectedEmail.retry_count > 0" class="detail-row">
            <span class="detail-row__label">Retries</span>
            <span class="detail-row__value">{{ selectedEmail.retry_count }} / {{ selectedEmail.max_retries }}</span>
          </div>
          <div v-if="selectedEmail.read_ip" class="detail-row">
            <span class="detail-row__label">Opened from IP</span>
            <span class="detail-row__value detail-row__value--mono">{{ selectedEmail.read_ip }}</span>
          </div>
        </div>

        <!-- Error message -->
        <div v-if="selectedEmail.error_message" class="detail-section">
          <div class="detail-section__title">Error</div>
          <div class="detail-error-box">{{ selectedEmail.error_message }}</div>
        </div>

        <!-- Spam report -->
        <div v-if="selectedEmail.spam_reported" class="detail-section">
          <div class="detail-section__title">Spam Report</div>
          <div class="detail-row">
            <span class="detail-row__label">Reported at</span>
            <span class="detail-dates__val">{{ formatDetailDate(selectedEmail.spam_reported_at) }}</span>
          </div>
          <div v-if="selectedEmail.spam_reported_by" class="detail-row">
            <span class="detail-row__label">Reported by</span>
            <span class="detail-row__value">{{ selectedEmail.spam_reported_by }}</span>
          </div>
        </div>

        <!-- Attachments -->
        <div v-if="emailAttachments.length || attachmentsLoading" class="detail-section">
          <div class="detail-section__title">Attachments</div>
          <div v-if="attachmentsLoading" class="mail-loading">
            <b-spinner small></b-spinner> Loading...
          </div>
          <div v-else class="detail-attachments">
            <div v-for="att in emailAttachments" :key="att.uuid" class="detail-attachment">
              <i class="fa-solid fa-paperclip detail-attachment__icon"></i>
              <a href="#" class="detail-attachment__name" @click.prevent="downloadAttachment(att)">{{ att.file_name }}</a>
              <span class="detail-attachment__size">{{ formatFileSize(att.file_size) }}</span>
              <b-spinner v-if="downloadingAttachment === att.uuid" small></b-spinner>
            </div>
          </div>
        </div>

        <!-- Body preview -->
        <div class="detail-section" style="border-bottom:none">
          <div class="detail-section__title">Body</div>
          <div class="detail-body-preview" v-html="selectedEmail.body"></div>
        </div>

        <!-- Actions -->
        <div v-if="(selectedEmail.status === 'failed' && $can('manage protokol-mail')) || (selectedEmail.status === 'pending' && $can('send protokol-mail'))" class="detail-actions">
          <button
            v-if="selectedEmail.status === 'failed'"
            class="detail-action-btn detail-action-btn--resend"
            @click="resendEmail(selectedEmail); showDetailModal = false"
          >
            <i class="fa-solid fa-rotate-right"></i> Resend
          </button>
          <button
            v-if="selectedEmail.status === 'pending'"
            class="detail-action-btn detail-action-btn--cancel"
            @click="cancelEmail(selectedEmail); showDetailModal = false"
          >
            <i class="fa-solid fa-ban"></i> Cancel Email
          </button>
        </div>
      </div>
    </main-dialog>

    <!-- CANCEL CONFIRMATION DIALOG -->
    <main-dialog
      :show="showCancelConfirm"
      title="Cancel Email"
      max-width="420px"
      @close="showCancelConfirm = false"
    >
      <p class="cancel-confirm__text">
        Are you sure you want to cancel this email to
        <strong>{{ cancelTarget && cancelTarget.to ? cancelTarget.to.join(', ') : '' }}</strong>?
      </p>
      <p class="cancel-confirm__hint">This action cannot be undone. The email will not be sent.</p>
      <div class="cancel-confirm__actions">
        <button class="cancel-confirm__btn cancel-confirm__btn--danger" @click="confirmCancel">
          <i class="fa-solid fa-ban"></i> Cancel Email
        </button>
        <button class="cancel-confirm__btn cancel-confirm__btn--dismiss" @click="showCancelConfirm = false">
          Keep
        </button>
      </div>
    </main-dialog>

    <!-- COMPOSE SIDEBAR -->
    <b-sidebar title="Compose Email" v-model="showSidebar" right width="600px">
      <div class="sidebar-body">
        <b-form @submit.prevent="sendEmail">

          <div class="sidebar-field">
            <label class="sidebar-label">To <span class="sidebar-required">*</span></label>
            <b-form-tags
              v-model="newEmail.to"
              placeholder="Enter email addresses"
              separator=" ,;"
              remove-on-delete
              :tag-validator="emailValidator"
            ></b-form-tags>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">CC <span class="sidebar-optional">(optional)</span></label>
            <b-form-tags
              v-model="newEmail.cc"
              placeholder="Enter email addresses"
              separator=" ,;"
              remove-on-delete
              :tag-validator="emailValidator"
            ></b-form-tags>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">BCC <span class="sidebar-optional">(optional)</span></label>
            <b-form-tags
              v-model="newEmail.bcc"
              placeholder="Enter email addresses"
              separator=" ,;"
              remove-on-delete
              :tag-validator="emailValidator"
            ></b-form-tags>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Reply-To <span class="sidebar-optional">(optional)</span></label>
            <b-form-input
              v-model="newEmail.reply_to"
              type="email"
              placeholder="reply@example.com"
            ></b-form-input>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Subject <span class="sidebar-required">*</span></label>
            <b-form-input
              v-model="newEmail.subject"
              placeholder="Email subject"
            ></b-form-input>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Sender name <span class="sidebar-optional">(optional)</span></label>
            <b-form-input
              v-model="newEmail.sender_name"
              placeholder="Your Name or Company"
            ></b-form-input>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Body <span class="sidebar-required">*</span></label>
            <CodeEditor
              v-model="newEmail.body"
              :withButton="false"
              :height="280"
              :options="{ mode: 'html' }"
              placeholder="Email body (HTML supported)"
            />
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Attachments <span class="sidebar-optional">(optional)</span></label>
            <b-form-file
              v-model="newEmail.attachments"
              multiple
              placeholder="Choose files or drop them here..."
              drop-placeholder="Drop files here..."
              :file-name-formatter="formatAttachmentNames"
            ></b-form-file>
            <small v-if="newEmail.attachments && newEmail.attachments.length" class="sidebar-hint">
              {{ newEmail.attachments.length }} file(s) — {{ formatTotalSize(newEmail.attachments) }}
            </small>
            <small class="sidebar-hint">Max 25 MB total. Attachments stored for 7 days.</small>
          </div>

          <b-alert
            v-if="sendEmailError"
            variant="danger"
            show
            dismissible
            @dismissed="sendEmailError = null"
          >{{ sendEmailError }}</b-alert>

          <div class="sidebar-actions">
            <button
              type="submit"
              class="sidebar-btn-send"
              :disabled="!formValid || sendEmailLoading"
            >
              <b-spinner v-if="sendEmailLoading" small></b-spinner>
              <span v-else><i class="fa-solid fa-paper-plane"></i> Send</span>
            </button>
            <button type="button" class="sidebar-btn-cancel" @click="showSidebar = false">Cancel</button>
          </div>

        </b-form>
      </div>
    </b-sidebar>

    <!-- STATS -->
    <div class="mail-stats">
      <div class="mail-stats__card">
        <div class="mail-stats__value">{{ stats.sent }}</div>
        <div class="mail-stats__label">Sent</div>
      </div>
      <div class="mail-stats__card">
        <div class="mail-stats__value">{{ stats.pending }}</div>
        <div class="mail-stats__label">Pending</div>
      </div>
      <div class="mail-stats__card">
        <div class="mail-stats__value">{{ stats.failed }}</div>
        <div class="mail-stats__label">Failed</div>
      </div>
      <div class="mail-stats__card">
        <div class="mail-stats__value">{{ stats.cancelled }}</div>
        <div class="mail-stats__label">Cancelled</div>
      </div>
    </div>

    <!-- MAIN CARD -->
    <div class="mail-card">

      <!-- Filter bar -->
      <div class="filter-bar">
        <span class="filter-bar__label">Status</span>
        <div class="filter-chips">
          <button
            v-for="opt in statusOptions"
            :key="opt.value"
            class="filter-chip"
            :class="{ 'filter-chip--active': emailStatus === opt.value }"
            @click="emailStatus = opt.value"
          >{{ opt.label }}</button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="emailsLoading" class="mail-loading">
        <b-spinner small></b-spinner> Loading emails...
      </div>

      <!-- Empty -->
      <div v-else-if="emails.length === 0" class="mail-empty">
        <div class="mail-empty__icon"><i class="fa-solid fa-envelope"></i></div>
        <p class="mail-empty__text">No emails found.</p>
      </div>

      <!-- Table -->
      <div v-else class="mail-table-wrap">
        <table class="mail-table">
          <thead>
            <tr>
              <th>To</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Opened</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="email in emails" :key="email.message_id" class="mail-table__row">
              <td><span class="email-to">{{ truncateTo(email.to) }}</span></td>
              <td><span class="email-subject">{{ email.subject }}</span></td>
              <td>
                <span class="status-badge" :class="'status-badge--' + email.status">{{ email.status }}</span>
              </td>
              <td>
                <span v-if="email.read_at" class="open-badge open-badge--yes" title="Opened">
                  <i class="fa-solid fa-envelope-open"></i>
                </span>
                <span v-else class="open-badge" title="Not opened">
                  <i class="fa-solid fa-envelope"></i>
                </span>
              </td>
              <td><span class="email-date">{{ email.formattedDate }}</span></td>
              <td class="email-actions">
                <div class="action-wrap">
                  <button
                    class="action-btn"
                    @click.stop="activeActionRow = activeActionRow === email.message_id ? null : email.message_id"
                  >
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                  <div v-if="activeActionRow === email.message_id" class="action-dropdown" @click.stop>
                    <button class="action-dropdown__item" @click="viewEmailDetails(email.message_id); activeActionRow = null">
                      <i class="fa-solid fa-eye"></i> View details
                    </button>
                    <button
                      v-if="email.status === 'failed' && $can('manage protokol-mail')"
                      class="action-dropdown__item"
                      @click="resendEmail(email); activeActionRow = null"
                    >
                      <i class="fa-solid fa-rotate-right"></i> Resend
                    </button>
                    <button
                      v-if="email.status === 'pending' && $can('send protokol-mail')"
                      class="action-dropdown__item action-dropdown__item--danger"
                      @click="cancelEmail(email); activeActionRow = null"
                    >
                      <i class="fa-solid fa-ban"></i> Cancel
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="emails.length > 0" class="mail-pagination">
        <span class="mail-pagination__count">
          Showing {{ emails.length }} of {{ emailsTotalCount || 0 }}
        </span>
        <div class="mail-pagination__nav">
          <button
            class="page-nav-btn"
            :disabled="emailsCurrentPage <= 1"
            @click="emailsCurrentPage--"
          ><i class="fa-solid fa-chevron-left"></i></button>
          <span class="page-nav-current">{{ emailsCurrentPage }}</span>
          <button
            class="page-nav-btn"
            :disabled="emailsCurrentPage >= totalPages"
            @click="emailsCurrentPage++"
          ><i class="fa-solid fa-chevron-right"></i></button>
        </div>
      </div>

    </div><!-- /.mail-card -->
  </div>
</template>

<script>
import MainDialog from "../../../MainDialog.vue";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormTags,
  BAlert,
  BSpinner,
} from "bootstrap-vue";
import CodeEditor from "@/components/form/Editor/Code";

export default {
  name: "MailIndex",
  components: {
    MainDialog,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormTags,
    BAlert,
    BSpinner,
    CodeEditor,
  },

  data() {
    return {
      emailsLoading: false,
      emailsError: null,
      emails: [],
      emailsTotalCount: null,
      emailsCurrentPage: 1,
      emailsPageSize: 20,
      emailStatus: "",
      currentParams: {},

      statusOptions: [
        { value: "", label: "Any" },
        { value: "pending", label: "Pending" },
        { value: "sent", label: "Sent" },
        { value: "failed", label: "Failed" },
        { value: "cancelled", label: "Cancelled" },
      ],

      // Compose sidebar
      showSidebar: false,
      newEmail: {
        to: [],
        cc: [],
        bcc: [],
        reply_to: "",
        subject: "",
        body: "",
        sender_name: "",
        attachments: [],
      },
      sendEmailLoading: false,
      sendEmailError: null,

      // Detail dialog
      showDetailModal: false,
      selectedEmail: null,
      detailsLoading: false,
      detailsError: null,
      emailAttachments: [],
      attachmentsLoading: false,
      downloadingAttachment: null,

      activeActionRow: null,

      // Cancel confirmation
      showCancelConfirm: false,
      cancelTarget: null,

      // Stats
      stats: { sent: 0, pending: 0, failed: 0, cancelled: 0 },
    };
  },

  computed: {
    formValid() {
      return (
        this.newEmail.to.length > 0 &&
        this.newEmail.subject.trim() !== "" &&
        this.newEmail.body.trim() !== ""
      );
    },
    totalPages() {
      if (!this.emailsTotalCount || !this.emailsPageSize) return 1;
      return Math.ceil(this.emailsTotalCount / this.emailsPageSize);
    },
  },

  created() {
    this.fetchEmails();
    this.fetchStats();
    this._closeDropdown = function() {
      this.activeActionRow = null;
    }.bind(this);
    document.addEventListener("click", this._closeDropdown);
  },

  beforeDestroy() {
    document.removeEventListener("click", this._closeDropdown);
  },

  methods: {
    emailValidator(tag) {
      var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(tag);
    },

    truncateTo(to) {
      if (!to || !to.length) return "—";
      var joined = to.join(", ");
      return joined.length > 42 ? joined.substring(0, 42) + "…" : joined;
    },

    formatDetailDate(val) {
      if (!val) return "—";
      var d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    copyToClipboard(text) {
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        var el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      var k = 1024;
      var sizes = ["B", "KB", "MB", "GB"];
      var i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    formatAttachmentNames(files) {
      if (files.length === 1) return files[0].name;
      return files.length + " files selected";
    },

    formatTotalSize(files) {
      var total = 0;
      for (var i = 0; i < files.length; i++) {
        total += files[i].size;
      }
      return this.formatFileSize(total);
    },

    async fetchEmails() {
      this.emailsLoading = true;
      this.emailsError = null;
      var params = {
        status: this.emailStatus || undefined,
        limit: this.emailsPageSize,
        page: this.emailsCurrentPage,
      };
      var paramStr = JSON.stringify(params);
      this.currentParams = paramStr;
      try {
        var resp = await this.$integrationApi.get("/protokol-mail/emails", { params: params });
        if (this.currentParams === paramStr) {
          var items = resp.data.items || [];
          this.emailsTotalCount = resp.data.total || 0;
          this.emails = items.map(function(email) {
            var date = email.sent_at
              ? new Date(email.sent_at).toLocaleString()
              : email.created_at
              ? new Date(email.created_at).toLocaleString()
              : "—";
            return Object.assign({}, email, { formattedDate: date });
          });
        }
      } catch (err) {
        this.emailsError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to load emails.";
      } finally {
        this.emailsLoading = false;
      }
    },

    resetComposeForm() {
      this.newEmail = {
        to: [],
        cc: [],
        bcc: [],
        reply_to: "",
        subject: "",
        body: "",
        sender_name: "",
        attachments: [],
      };
      this.sendEmailError = null;
      this.sendEmailLoading = false;
    },

    async sendEmail() {
      this.sendEmailError = null;
      this.sendEmailLoading = true;
      try {
        if (this.newEmail.attachments && this.newEmail.attachments.length > 0) {
          var formData = new FormData();
          var self = this;
          self.newEmail.to.forEach(function(e) { formData.append("to", e); });
          self.newEmail.cc.forEach(function(e) { formData.append("cc", e); });
          self.newEmail.bcc.forEach(function(e) { formData.append("bcc", e); });
          formData.append("subject", self.newEmail.subject);
          formData.append("body", self.newEmail.body);
          if (self.newEmail.reply_to) formData.append("reply_to", self.newEmail.reply_to);
          if (self.newEmail.sender_name) formData.append("sender_name", self.newEmail.sender_name);
          self.newEmail.attachments.forEach(function(f) { formData.append("attachments", f, f.name); });
          await this.$integrationApi.post("/protokol-mail/emails", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await this.$integrationApi.post("/protokol-mail/emails", {
            to: this.newEmail.to,
            cc: this.newEmail.cc.length ? this.newEmail.cc : undefined,
            bcc: this.newEmail.bcc.length ? this.newEmail.bcc : undefined,
            reply_to: this.newEmail.reply_to || undefined,
            subject: this.newEmail.subject,
            body: this.newEmail.body,
            sender_name: this.newEmail.sender_name || undefined,
          });
        }
        this.$bvToast.toast("Email queued for sending!", {
          variant: "success",
          title: "Email Sent",
        });
        this.showSidebar = false;
        this.fetchEmails();
        this.fetchStats();
      } catch (err) {
        this.sendEmailError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to send email.";
      } finally {
        this.sendEmailLoading = false;
      }
    },

    async viewEmailDetails(messageID) {
      this.showDetailModal = true;
      this.detailsLoading = true;
      this.detailsError = null;
      this.selectedEmail = null;
      this.emailAttachments = [];
      try {
        var resp = await this.$integrationApi.get("/protokol-mail/emails/" + messageID);
        this.selectedEmail = resp.data;
        this.fetchAttachments(messageID);
      } catch (err) {
        this.detailsError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to load email details.";
      } finally {
        this.detailsLoading = false;
      }
    },

    async fetchAttachments(messageID) {
      this.attachmentsLoading = true;
      try {
        var resp = await this.$integrationApi.get(
          "/protokol-mail/emails/" + messageID + "/attachments"
        );
        this.emailAttachments = resp.data || [];
      } catch (err) {
        this.emailAttachments = [];
      } finally {
        this.attachmentsLoading = false;
      }
    },

    async downloadAttachment(attachment) {
      this.downloadingAttachment = attachment.uuid;
      try {
        var messageID = this.selectedEmail.message_id;
        var resp = await this.$integrationApi.get(
          "/protokol-mail/emails/" + messageID + "/attachments/" + attachment.uuid,
          { responseType: "blob" }
        );
        var url = window.URL.createObjectURL(new Blob([resp.data]));
        var link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", attachment.file_name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$bvToast.toast("Failed to download attachment", {
          variant: "danger",
          title: "Error",
        });
      } finally {
        this.downloadingAttachment = null;
      }
    },

    async resendEmail(email) {
      try {
        await this.$integrationApi.post(
          "/protokol-mail/emails/" + email.message_id + "/resend"
        );
        this.$bvToast.toast("Email queued for resend!", {
          variant: "success",
          title: "Resent",
        });
        this.fetchEmails();
        this.fetchStats();
      } catch (err) {
        this.emailsError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to resend email.";
      }
    },

    cancelEmail(email) {
      this.cancelTarget = email;
      this.showCancelConfirm = true;
    },

    async confirmCancel() {
      var email = this.cancelTarget;
      this.showCancelConfirm = false;
      this.cancelTarget = null;
      if (!email) return;
      try {
        await this.$integrationApi.post(
          "/protokol-mail/emails/" + email.message_id + "/cancel"
        );
        this.$bvToast.toast("Email cancelled.", {
          variant: "success",
          title: "Cancelled",
        });
        this.fetchEmails();
        this.fetchStats();
      } catch (err) {
        this.emailsError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to cancel email.";
      }
    },

    async fetchStats() {
      try {
        var resp = await this.$integrationApi.get("/protokol-mail/stats");
        this.stats = resp.data || { sent: 0, pending: 0, failed: 0, cancelled: 0 };
      } catch (err) {
        // silently fail — stats are non-critical
      }
    },
  },

  watch: {
    emailsCurrentPage: function() {
      this.fetchEmails();
    },
    emailStatus: function() {
      this.emailsCurrentPage = 1;
      this.fetchEmails();
    },
  },
};
</script>

<style scoped>
/* ── PAGE ─────────────────────────────────────────── */
.mail-page {
  padding: 8px 0;
}

/* ── HEADER ───────────────────────────────────────── */
.mail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.mail-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.mail-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}
.mail-header__title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.mail-header__sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
}
.btn-compose {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  border-radius: 10px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
  font-size: 0.9rem;
}
.btn-compose:hover { opacity: 0.88; }

/* ── STATS ─────��──────────────────────────────────── */
.mail-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.mail-stats__card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
}
.mail-stats__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}
.mail-stats__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-top: 4px;
}

/* ── CARD ─────────────────────────────────────────── */
.mail-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  overflow: hidden;
}

/* ── FILTER BAR ───────────────────────────────────── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-default);
}
.filter-bar__label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}
.filter-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.filter-chip {
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}
.filter-chip:hover { border-color: #8b5cf6; color: #8b5cf6; }
.filter-chip--active {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}

/* ── LOADING / EMPTY ──────────────────────────────── */
.mail-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-muted);
  justify-content: center;
}
.mail-empty {
  text-align: center;
  padding: 60px 20px;
}
.mail-empty__icon {
  font-size: 2.5rem;
  color: var(--text-muted);
  margin-bottom: 12px;
  opacity: 0.4;
}
.mail-empty__text { color: var(--text-muted); }

/* ── TABLE ────────────────────────────────────────── */
.mail-table-wrap { overflow-x: auto; }
.mail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.mail-table thead tr { border-bottom: 1px solid var(--border-default); }
.mail-table th {
  padding: 12px 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}
.mail-table__row {
  border-bottom: 1px solid var(--border-default);
  transition: background 0.12s;
}
.mail-table__row:last-child { border-bottom: none; }
.mail-table__row:hover { background: rgba(255,255,255,0.03); }
.mail-table td {
  padding: 14px 20px;
  color: var(--text-primary);
  vertical-align: middle;
}
.email-to {
  font-size: 0.85rem;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.email-subject {
  font-size: 0.85rem;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.email-date {
  color: var(--text-muted);
  font-size: 0.82rem;
  white-space: nowrap;
}
.email-actions { text-align: right; width: 48px; }
.open-badge {
  font-size: 0.82rem;
  color: var(--text-muted);
}
.open-badge--yes { color: #38ef7d; }

/* ── STATUS BADGES ────────────────────────────────── */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}
.status-badge--sent      { background: rgba(17,153,142,0.18); color: #38ef7d; }
.status-badge--pending   { background: rgba(251,189,8,0.15);  color: #fbbf08; }
.status-badge--failed    { background: rgba(239,68,68,0.15);  color: #f87171; }
.status-badge--cancelled { background: rgba(148,163,184,0.15); color: #94a3b8; }

/* ── ACTION DROPDOWN ──────────────────────────────── */
.action-wrap { position: relative; display: inline-block; }
.action-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  line-height: 1;
}
.action-btn:hover { background: rgba(255,255,255,0.06); color: var(--text-primary); }
.action-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 1000;
  background: #1e2433;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 6px;
  min-width: 160px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
}
.action-dropdown__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  color: #c8d3e6;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;
  transition: background 0.12s, color 0.12s;
}
.action-dropdown__item:hover { background: rgba(255,255,255,0.07); color: #fff; }
.action-dropdown__item i { width: 14px; text-align: center; color: #64748b; }
.action-dropdown__item:hover i { color: #94a3b8; }
.action-dropdown__item--danger:hover { color: #f87171; }
.action-dropdown__item--danger:hover i { color: #f87171; }

/* ── PAGINATION ───────────────────────────────────── */
.mail-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid var(--border-default);
}
.mail-pagination__count {
  font-size: 0.8rem;
  color: var(--text-muted);
}
.mail-pagination__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}
.page-nav-btn {
  background: none;
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  border-radius: 7px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  font-size: 0.8rem;
}
.page-nav-btn:hover:not(:disabled) { background: rgba(255,255,255,0.06); color: var(--text-primary); }
.page-nav-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-nav-current {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 28px;
  text-align: center;
}

/* ── COMPOSE SIDEBAR ──────────────────────────────── */
.sidebar-body { padding: 20px; }
.sidebar-field { margin-bottom: 18px; }
.sidebar-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}
.sidebar-required { color: #f87171; font-weight: 700; }
.sidebar-optional {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.78rem;
}
.sidebar-hint {
  display: block;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 4px;
}
.sidebar-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.sidebar-btn-send {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 20px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.sidebar-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
.sidebar-btn-send:hover:not(:disabled) { opacity: 0.88; }
.sidebar-btn-cancel {
  padding: 9px 16px;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.88rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
}
.sidebar-btn-cancel:hover { color: var(--text-primary); border-color: rgba(255,255,255,0.25); }

/* ── DETAIL DIALOG ────────────────────────────────── */
.detail-error {
  color: #f87171;
  font-size: 0.88rem;
  padding: 8px 0;
}
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-default);
}
.detail-subject {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  margin-bottom: 8px;
}
.detail-header__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.detail-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.detail-tag--opened {
  background: rgba(52,211,153,0.15);
  border: 1px solid rgba(52,211,153,0.3);
  color: #34d399;
}
.detail-tag--spam {
  background: rgba(248,113,113,0.15);
  border: 1px solid rgba(248,113,113,0.3);
  color: #f87171;
}
.detail-dates { text-align: right; flex-shrink: 0; }
.detail-dates__row {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 3px;
}
.detail-dates__label {
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.detail-dates__val { font-size: 0.78rem; color: var(--text-primary); }
.detail-section {
  padding: 14px 0;
  border-bottom: 1px solid var(--border-default);
}
.detail-section__title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 10px;
}
.detail-row { margin-bottom: 12px; }
.detail-row:last-child { margin-bottom: 0; }
.detail-row__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 3px;
}
.detail-row__value { font-size: 0.88rem; color: var(--text-primary); word-break: break-all; }
.detail-row__value--mono { font-family: monospace; font-size: 0.82rem; color: #94a3b8; }
.detail-row__copy-row { display: flex; align-items: center; gap: 8px; }
.detail-copy-btn {
  background: none;
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 0.78rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
  flex-shrink: 0;
}
.detail-copy-btn:hover { color: var(--text-primary); border-color: rgba(255,255,255,0.25); }
.detail-error-box {
  background: rgba(248,113,113,0.1);
  border: 1px solid rgba(248,113,113,0.3);
  border-radius: 8px;
  padding: 10px 14px;
  color: #f87171;
  font-size: 0.85rem;
  word-break: break-word;
}
.detail-attachments { display: flex; flex-direction: column; gap: 8px; }
.detail-attachment {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--border-default);
  border-radius: 8px;
}
.detail-attachment__icon { color: var(--text-muted); font-size: 0.85rem; }
.detail-attachment__name {
  flex: 1;
  color: #a5b4fc;
  font-size: 0.85rem;
  text-decoration: none;
}
.detail-attachment__name:hover { text-decoration: underline; }
.detail-attachment__size { font-size: 0.75rem; color: var(--text-muted); }
.detail-body-preview {
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 14px;
  max-height: 360px;
  overflow-y: auto;
  font-size: 0.88rem;
  color: var(--text-primary);
}
.detail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 16px;
}
.detail-action-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 9px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.83rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.detail-action-btn:hover { background: rgba(255,255,255,0.05); }
.detail-action-btn--resend:hover { border-color: #a5b4fc; color: #a5b4fc; }
.detail-action-btn--cancel:hover { border-color: #f87171; color: #f87171; }

/* ── CANCEL CONFIRM DIALOG ───────────────────────── */
.cancel-confirm__text {
  font-size: 0.92rem;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.cancel-confirm__hint {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 18px;
}
.cancel-confirm__actions {
  display: flex;
  gap: 10px;
}
.cancel-confirm__btn {
  padding: 8px 16px;
  border-radius: 9px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}
.cancel-confirm__btn:hover { opacity: 0.85; }
.cancel-confirm__btn--danger {
  background: rgba(239,68,68,0.15);
  color: #f87171;
  border: 1px solid rgba(239,68,68,0.3);
}
.cancel-confirm__btn--dismiss {
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-default);
}

/* ── FORM-TAGS DARK FIX ───────────────────────────── */
.b-form-tags {
  background-color: var(--bg-card) !important;
  color: var(--text-primary) !important;
  border-color: var(--border-default) !important;
}
.b-form-tags .b-form-tags-input {
  background-color: transparent !important;
  color: var(--text-primary) !important;
}
.b-form-tags .b-form-tag {
  background-color: rgba(99,102,241,0.2) !important;
  color: #c4b5fd !important;
  border: 1px solid rgba(99,102,241,0.4) !important;
}
</style>
